import {
  Box,
  createTheme,
  PaletteMode,
  ThemeProvider,
  useTheme,
} from "@mui/material";
import { useThemeMode } from "../theme/ThemeModeProviderContext";
import { useMemo } from "react";
import useGetTopNavMenu from "@features/unified-user-header-feature/hooks/useGetTopNavMenu";
import CourtesyNavigationHeader from "@features/unified-user-header-feature/CourtesyNavigationHeader";
import { TopNavMenu } from "@features/unified-user-header-feature/TopNavMenu";
import { HEADER_HEIGHT_CSS_VAR } from "shared-ui/src/constants";
import { useClaims } from "auth/ClaimsProvider";
import {
  isImpersonatingAccount,
  isImpersonatingUser,
} from "shared-ui/src/utils/clientmanagement";
import { ImpersonationPin } from "@features/impersonation-feature/components/ImpersonationPin";
import { isLocalCxHost } from "../functions/urlHelper";

export default function UnifiedHeader() {
  const { getClaimValue, hasRoleIn, isShi } = useClaims();
  const baseTheme = useTheme();
  const { mode } = useThemeMode();
  const { data: topNavItems } = useGetTopNavMenu();

  const firstName = getClaimValue("firstName");
  const lastName = getClaimValue("lastName");
  const emailAddress = getClaimValue("userEmail");
  const displayName = [firstName, lastName].filter(Boolean).join(" ");

  const isImpersonating = isImpersonatingAccount() || isImpersonatingUser();

  const isLocalShi = isShi();
  const isShiUser =
    hasRoleIn(
      "ShiUser",
      "ShiAdmin",
      "ShiResource",
      "ShiImpersonateUser",
      "ShiImpersonateVirtualAdmin",
    ) || isLocalShi;
  const showImpersonationBar =
    isImpersonating || (isLocalCxHost() && isShiUser);

  const unifiedHeaderTheme = useMemo(
    () =>
      createTheme({
        ...baseTheme,
        palette: {
          ...baseTheme.palette,
          mode: mode as PaletteMode,
          common: {
            ...baseTheme.palette.common,
            white: "#FFFFFF",
          },
          background: {
            ...baseTheme.palette.background,
            paper: mode === "light" ? "#F4F6F7" : "#1E1E1E",
            default: mode === "light" ? "#FFFFFF" : "#121212",
          },
          text: {
            ...baseTheme.palette.text,
            primary: mode === "light" ? "#253746" : "#E0E0E0",
          },
        },
      }),
    [baseTheme, mode],
  );

  return (
    <ThemeProvider theme={unifiedHeaderTheme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflow: "hidden",
        }}
      >
        {showImpersonationBar && <ImpersonationPin />}
        <CourtesyNavigationHeader
          isShiUser={isShiUser}
          displayName={displayName || undefined}
          emailAddress={emailAddress ?? ""}
          firstName={firstName ?? ""}
          lastName={lastName ?? ""}
        />
        {/* Use padding (not margin) so this offset can't collapse into an ancestor's box
            and inflate the document's scrollable height (margin-top here would collapse
            up through #root, adding phantom overflow and a spurious page scrollbar). */}
        <Box sx={{ pt: `var(${HEADER_HEIGHT_CSS_VAR}, 108px)` }}>
          <TopNavMenu topNavMenuItems={topNavItems ?? []} />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
